import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import CustomToggleSwitch from "../../../../components/common/CustomToggle";
import CustomModal from "../../../../components/common/Modal";
import CustomTable from "../../../../components/common/CustomTable";
import { useToggleState } from "../../../../Hooks/useToggleState";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomDropdown from "../../../../components/common/CustomDropdown/CustomDropdown";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import {
   addSingleCustomerChildAsync,
   fetchChildTableDataAsync,
   fetchSingleCustomerMasterAsync,
   updateSingleCustomerChildAsync,
} from "../../../../redux/features/customerMaster";
import toast from "react-hot-toast";
import * as yup from "yup";
import validation from "../../../../helpers/validation";
import { BsPencilSquare } from "react-icons/bs";

const validationSchema = yup.object().shape({
   laneName: yup.string().required("Lane name is required"),
   origin: yup.string().required("Origin is required"),
   destination: yup.string().required("Destination is required"),
   vehicleType: yup.string().required("Vehicle Type is required"),
   tonnage: yup.string().required("Tonnage is required"),
   expressModeRateAdditional: validation.amount("Express Mode Rate Additional"),
   superExpressModeRateAdditional: validation.amount(
      "Super Express Mode Rate Additional"
   ),
   detentionRateAdditional: validation.amount("Detention Rate Additional"),
   multipleLoadingLocationRateAdditional: validation.amount(
      "Multiple Loading Location Rate Additional"
   ),
   multipleUnloadingLocationRateAdditional: validation.amount(
      "Multiple Unloading Location Rate Additional"
   ),
   loadingCharges: validation.amount("Loading Charges"),
   unloadingCharge: validation.amount("Unloading Charge"),
   miscellaneousCharges: validation.amount("Miscellaneous Charges"),
   brokerageCharges: validation.amount("Brokerage Charges"),
   miscellaneousRemarks: yup.string().required("Miscellaneous Remarks is required"),
   tripRate: validation.amount("Trip Rate"),
   dieselRate: validation.amount("Diesel Rate"), // New field
   adblueRate: validation.amount("Adblue Rate"), // New field
   tripAdvance: validation.amount("Trip Advance"), // New field
   miscellaneousRate: validation.amount("Miscellaneous Rate"), // New field
});

const initialState = {
   id: "",
   laneName: "",
   origin: "",
   destination: "",
   vehicleType: "",
   tonnage: "",
   expressModeRateAdditional: "",
   superExpressModeRateAdditional: "",
   detentionRateAdditional: "",
   multipleLoadingLocationRateAdditional: "",
   multipleUnloadingLocationRateAdditional: "",
   loadingCharges: "",
   unloadingCharge: "",
   miscellaneousCharges: "",
   brokerageCharges: "",
   miscellaneousRemarks: "",
   originValue: [{ label: "", value: "" }],
   destinationValue: [{ label: "", value: "" }],
   vehicleTypeValue: [{ label: "", value: "" }],
   tonnageValue: [{ label: "", value: "" }],
   tripRate: "",
   dieselRate: "", // New field
   adblueRate: "", // New field
   tripAdvance: "", // New field
   miscellaneousRate: "", // New field
};

const Lane = ({
   laneList,
   tableName,
   locationData,
   vehicleTypeData,
   tonnageData,
   customerName,
}) => {
   const params = useParams();
   const dispatch = useDispatch();

   const [action, setAction] = useState("");
   const [isChecked, setIsChecked] = useState({}); // State to manage toggle
   const [isModalVisible, handleShowModal, handleCloseModal] = useToggleState();
   const [currentIndex, setCurrentIndex] = useState(null);

   const formik = useFormik({
      initialValues: initialState,
      enableReinitialize: true,
      validationSchema,
      onSubmit: action === "add" ? handleLaneAdd : handleLaneUpdate,
   });

   const {
      touched,
      errors,
      values,
      setValues,
      getFieldProps,
      handleSubmit,
      resetForm,
      setFieldValue,
   } = formik;

   // Generate lane name
   const { destinationValue, originValue, vehicleTypeValue, tonnageValue } = values;
   useEffect(() => {
      setFieldValue(
         "laneName",
         `${customerName}#${originValue?.[currentIndex]?.label ?? ""}#${
            destinationValue[currentIndex]?.label ?? ""
         }#${vehicleTypeValue?.[currentIndex]?.label ?? ""}#${
            tonnageValue?.[currentIndex]?.label ?? ""
         }`
      );
   }, [
      destinationValue,
      originValue,
      vehicleTypeValue,
      tonnageValue,
      customerName,
      currentIndex,
   ]);

   // Toogle Switch
   const toggleSwitch = () => {
      setIsChecked((prev) => !prev);
   };

   // Edit button click handler
   const handleEditButtonClick = (item, index) => {
      setCurrentIndex(index);
      setAction("edit");
      dispatch(backdropLoadingAction(true));
      dispatch(
         fetchChildTableDataAsync({
            id: item?.id,
            tableName: tableName,
            err: () => {
               dispatch(backdropLoadingAction(false));
            },
            done: (res) => {
               dispatch(backdropLoadingAction(false));
               setValues({
                  id: item?.id,
                  laneName: item?.name,
                  origin: item?.origin,
                  destination: item?.destination,
                  vehicleType: item?.vehicle_category_id,
                  tonnage: item?.tonnage_id,
                  expressModeRateAdditional: item?.cust_express_mode_rate_additional,
                  superExpressModeRateAdditional:
                     item?.cust_super_express_mode_rate_additional,
                  detentionRateAdditional: item?.cust_detention_rate_additional,
                  multipleLoadingLocationRateAdditional:
                     item?.cust_multiple_loading_location_rate_additional,
                  multipleUnloadingLocationRateAdditional:
                     item?.cust_multiple_unloading_location_rate_additional,
                  loadingCharges: item?.cust_loading_charges,
                  unloadingCharge: item?.cust_unloading_charge,
                  miscellaneousCharges: item?.cust_miscellaneous_charges,
                  brokerageCharges: item?.cust_brokerage_charges,
                  miscellaneousRemarks: item?.cust_miscellaneous_remarks,
                  originValue: locationData?.filter?.(
                     (loc) => loc?.value === item?.origin
                  ),
                  destinationValue: locationData?.filter?.(
                     (loc) => loc?.value === item?.destination
                  ),
                  vehicleTypeValue: vehicleTypeData?.filter?.(
                     (v) => v?.value === item?.vehicle_category_id
                  ),
                  tonnageValue: tonnageData?.filter?.(
                     (t) => t?.value === item?.tonnage_id
                  ),
                  tripRate: item?.cust_trip_rate,
                  dieselRate: item?.diesel_rate, // New field
                  adblueRate: item?.adblue_rate, // New field
                  tripAdvance: item?.trip_advance, // New field
                  miscellaneousRate: item?.miscellaneous_rate, // New field
               });
               handleShowModal();
            },
         })
      );
   };

   // Add button click handler
   const handleAddButtonClick = () => {
      setCurrentIndex(laneList?.length - 1);
      setValues({
         ...values,
         originValue: [...values.originValue, { label: "", value: "" }],
         destinationValue: [...values.originValue, { label: "", value: "" }],
         vehicleTypeValue: [...values.originValue, { label: "", value: "" }],
         tonnageValue: [...values.originValue, { label: "", value: "" }],
      });
      setAction("add");
      handleShowModal();
   };

   // Update handler
   function handleLaneUpdate(data) {
      const newData = new FormData();

      const _data = {
         lane_master_id: data?.id,
         lane_name: data?.laneName,
         origin: data?.origin,
         destination: data?.destination,
         vehicle_category_id: data?.vehicleType,
         tonnage_id: data?.tonnage,
         cust_express_mode_rate_additional: data?.expressModeRateAdditional,
         cust_super_express_mode_rate_additional: data?.superExpressModeRateAdditional,
         cust_detention_rate_additional: data?.detentionRateAdditional,
         cust_multiple_loading_location_rate_additional:
            data?.multipleLoadingLocationRateAdditional,
         cust_loading_charges: data?.loadingCharges,
         cust_miscellaneous_charges: data?.miscellaneousCharges,
         cust_brokerage_charges: data?.brokerageCharges,
         cust_unloading_charge: data?.unloadingCharge,
         cust_miscellaneous_remarks: data?.miscellaneousRemarks,
         cust_multiple_unloading_location_rate_additional:
            data?.multipleUnloadingLocationRateAdditional,
         cust_trip_rate: data?.tripRate,
         diesel_rate: data?.dieselRate, // New field
         adblue_rate: data?.adblueRate, // New field
         trip_advance: data?.tripAdvance, // New field
         miscellaneous_rate: data?.miscellaneousRate, // New field
      };

      newData.append("customer_master_id", params?.id);

      Object.keys(_data).forEach((key) => {
         newData.append(`${key}[]`, _data[key]);
      });

      newData.append("_method", "PATCH");
      newData.append("model_id", 6);
      newData.append("action_id", 2);

      dispatch(backdropLoadingAction(true));

      dispatch(
         updateSingleCustomerChildAsync({
            data: newData,
            err: () => {
               toast.error("Something went wrong !");
               dispatch(backdropLoadingAction(false));
            },
            done: () => {
               toast.success("Record updated successfully !");
               resetForm();
               handleCloseModal();
               dispatch(
                  fetchSingleCustomerMasterAsync({
                     id: params?.id,
                     err: () => {
                        dispatch(backdropLoadingAction(false));
                     },
                     done: () => {
                        dispatch(backdropLoadingAction(false));
                     },
                  })
               );
            },
         })
      );
   }

   // Add handler
   function handleLaneAdd(data) {
      const newData = new FormData();

      const _data = {
         lane_name: data?.laneName,
         origin: data?.origin,
         destination: data?.destination,
         vehicle_category_id: data?.vehicleType,
         tonnage_id: data?.tonnage,
         cust_express_mode_rate_additional: data?.expressModeRateAdditional,
         cust_super_express_mode_rate_additional: data?.superExpressModeRateAdditional,
         cust_detention_rate_additional: data?.detentionRateAdditional,
         cust_multiple_loading_location_rate_additional:
            data?.multipleLoadingLocationRateAdditional,
         cust_loading_charges: data?.loadingCharges,
         cust_miscellaneous_charges: data?.miscellaneousCharges,
         cust_brokerage_charges: data?.brokerageCharges,
         cust_unloading_charge: data?.unloadingCharge,
         cust_miscellaneous_remarks: data?.miscellaneousRemarks,
         cust_multiple_unloading_location_rate_additional:
            data?.multipleUnloadingLocationRateAdditional,
         cust_trip_rate: data?.tripRate, // api pending
         diesel_rate: data?.dieselRate, // New field
         adblue_rate: data?.adblueRate, // New field
         trip_advance: data?.tripAdvance, // New field
         miscellaneous_rate: data?.miscellaneousRate, // New field
      };

      newData.append("customer_master_id", params?.id);
      Object.keys(_data).forEach((key) => {
         newData.append(`${key}[]`, _data[key]);
      });

      newData.append("model_id", 6);
      newData.append("action_id", 1);

      dispatch(backdropLoadingAction(true));

      dispatch(
         addSingleCustomerChildAsync({
            data: newData,
            err: () => {
               toast.error("Something went wrong !");
               dispatch(backdropLoadingAction(false));
            },
            done: () => {
               toast.success("Record added successfully !");
               resetForm();
               handleCloseModal();
               dispatch(
                  fetchSingleCustomerMasterAsync({
                     id: params?.id,
                     err: () => {
                        dispatch(backdropLoadingAction(false));
                     },
                     done: () => {
                        dispatch(backdropLoadingAction(false));
                     },
                  })
               );
            },
         })
      );
   }

   const columns = [
      {
         name: "Lane Name",
         selector: (row) => row.name,
         sortable: true,
      },
      {
         name: "Action",
         width: "9rem",
         selector: (row) => row.action,
      },
   ];

   const data = laneList?.map?.((item, index) => ({
      ...item,
      action: (
         <div className="d-flex align-items-center">
            <button className="btn" onClick={() => handleEditButtonClick(item, index)}>
               <BsPencilSquare
                  className="text-primary"
                  style={{ height: "1.5rem", width: "1.5rem" }}
               />
            </button>
            <CustomToggleSwitch
               checked={isChecked}
               onChange={toggleSwitch}
               id="csutomer6"
            />
         </div>
      ),
   }));

   return (
      <>
         <CustomModal
            showModal={isModalVisible}
            handleCloseModal={() => {
               resetForm();
               handleCloseModal();
            }}
            modalSize="modal-md"
            title="Lane"
            onSubmit={handleSubmit}
            child={
               <>
                  <div>
                     <CustomInput
                        disabled
                        require
                        label="Lane Name"
                        id="laneName"
                        placeholder="Lane Name"
                        {...getFieldProps(`laneName`)}
                        errors={touched?.laneName && errors?.laneName}
                        message={errors?.laneName}
                     />
                  </div>
                  <div>
                     <CustomDropdown
                        label="Origin"
                        require
                        optionData={
                           values?.destination
                              ? locationData?.filter(
                                   (l) => l.value !== values.destination
                                )
                              : locationData
                        }
                        values={locationData?.filter(
                           (item) => item.value === values?.origin
                        )}
                        {...getFieldProps("origin")}
                        onChange={(values) => {
                           setFieldValue(`origin`, values?.[0]?.value);
                           setFieldValue(`originValue[${currentIndex}]`, {
                              label: values?.[0]?.label,
                              value: values?.[0]?.value,
                           });
                        }}
                        errors={touched?.origin && errors?.origin}
                        message={errors?.origin}
                     />
                  </div>
                  <div>
                     <CustomDropdown
                        label="Destination"
                        require
                        optionData={
                           values?.origin
                              ? locationData?.filter((l) => l.value !== values.origin)
                              : locationData
                        }
                        values={locationData?.filter(
                           (item) => item.value === values?.destination
                        )}
                        onChange={(values) => {
                           setFieldValue(`destination`, values?.[0]?.value);
                           setFieldValue(`destinationValue[${currentIndex}]`, {
                              label: values?.[0]?.label,
                              value: values?.[0]?.value,
                           });
                        }}
                        errors={touched?.destination && errors?.destination}
                        message={errors?.destination}
                     />
                  </div>
                  {/* Vehicle Category Sec */}
                  <div>
                     <CustomDropdown
                        label="Vehicle Type"
                        require
                        optionData={vehicleTypeData}
                        values={vehicleTypeData?.filter(
                           (item) => item.value === values?.vehicleType
                        )}
                        onChange={(values) => {
                           setFieldValue(`vehicleType`, values?.[0]?.value);
                           setFieldValue(`vehicleTypeValue[${currentIndex}]`, {
                              label: values?.[0]?.label,
                              value: values?.[0]?.value,
                           });
                        }}
                        errors={touched?.vehicleType && errors?.vehicleType}
                        message={errors?.vehicleType}
                     />
                  </div>
                  {/* Tonnage Sec */}
                  <div>
                     <CustomDropdown
                        label="Tonnage(T)"
                        require
                        optionData={tonnageData}
                        values={tonnageData?.filter(
                           (item) => item.value === values?.tonnage
                        )}
                        onChange={(values) => {
                           setFieldValue(`tonnage`, values?.[0]?.value);
                           setFieldValue(`tonnageValue[${currentIndex}]`, {
                              label: values?.[0]?.label,
                              value: values?.[0]?.value,
                           });
                        }}
                        errors={touched?.tonnage && errors?.tonnage}
                        message={errors?.tonnage}
                     />
                  </div>

                  <h4>Details</h4>
                  <div>
                     <CustomInput
                        require
                        label="Express mode rate additional"
                        id="expressModeRateAdditional"
                        placeholder="Enter Amount"
                        {...getFieldProps(`expressModeRateAdditional`)}
                        errors={
                           touched?.expressModeRateAdditional &&
                           errors?.expressModeRateAdditional
                        }
                        message={errors?.expressModeRateAdditional}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="Super express mode rate additional"
                        id="superExpressModeRateAdditional"
                        placeholder="Enter amount"
                        {...getFieldProps(`superExpressModeRateAdditional`)}
                        errors={
                           touched?.superExpressModeRateAdditional &&
                           errors?.superExpressModeRateAdditional
                        }
                        message={errors?.superExpressModeRateAdditional}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="Detention rate additional"
                        id="detentionRateAdditional"
                        placeholder="Enter amount"
                        {...getFieldProps(`detentionRateAdditional`)}
                        errors={
                           touched?.detentionRateAdditional &&
                           errors?.detentionRateAdditional
                        }
                        message={errors?.detentionRateAdditional}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="Multiple loading location rate additional"
                        id="multipleLoadingLocationRateAdditional"
                        placeholder="Enter amount"
                        {...getFieldProps(`multipleLoadingLocationRateAdditional`)}
                        errors={
                           touched?.multipleLoadingLocationRateAdditional &&
                           errors?.multipleLoadingLocationRateAdditional
                        }
                        message={errors?.multipleLoadingLocationRateAdditional}
                     />
                  </div>

                  <div>
                     <CustomInput
                        require
                        label="Multiple unloading location rate additional"
                        id="multipleUnloadingLocationRateAdditional"
                        placeholder="Enter amount"
                        {...getFieldProps(`multipleUnloadingLocationRateAdditional`)}
                        errors={
                           touched?.multipleLoadingLocationRateAdditional &&
                           errors?.multipleLoadingLocationRateAdditional
                        }
                        message={errors?.multipleLoadingLocationRateAdditional}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="Loading charges"
                        id="loadingCharges"
                        placeholder="Enter amount"
                        {...getFieldProps(`loadingCharges`)}
                        errors={touched?.loadingCharges && errors?.loadingCharges}
                        message={errors?.loadingCharges}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="Unloading charge"
                        id="unloadingCharge"
                        placeholder="Enter amount"
                        {...getFieldProps(`unloadingCharge`)}
                        errors={touched?.unloadingCharge && errors?.unloadingCharge}
                        message={errors?.unloadingCharge}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="miscellaneous charges"
                        id="miscellaneousCharges"
                        placeholder="Enter amount"
                        {...getFieldProps(`miscellaneousCharges`)}
                        errors={
                           touched?.miscellaneousCharges && errors?.miscellaneousCharges
                        }
                        message={errors?.miscellaneousCharges}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="brokerage charges"
                        id="brokerageCharges"
                        placeholder="Enter amount"
                        {...getFieldProps(`brokerageCharges`)}
                        errors={
                           touched?.brokerageCharges && errors?.brokerageCharges
                        }
                        message={errors?.brokerageCharges}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="miscellaneous Remarks"
                        id="miscellaneousRemarks"
                        placeholder="Enter remarks"
                        {...getFieldProps(`miscellaneousRemarks`)}
                        errors={
                           touched?.miscellaneousRemarks && errors?.miscellaneousRemarks
                        }
                        message={errors?.miscellaneousRemarks}
                     />
                  </div>
                  <div>
                     <CustomInput
                        require
                        label="Trip Rate"
                        id="tripRate"
                        placeholder="Enter remarks"
                        {...getFieldProps(`tripRate`)}
                        errors={touched?.tripRate && errors?.tripRate}
                        message={errors?.tripRate}
                     />
                  </div>

                  <div>
                     <CustomInput
                        require
                        label="Diesel Liters"
                        id="dieselRate"
                        placeholder="Enter diesel rate"
                        {...getFieldProps(`dieselRate`)}
                        errors={touched?.dieselRate && errors?.dieselRate}
                        message={errors?.dieselRate}
                     />
                  </div>

                  <div>
                     <CustomInput
                        require
                        label="Adblue Liters"
                        id="adblueRate"
                        placeholder="Enter adblue rate"
                        {...getFieldProps(`adblueRate`)}
                        errors={touched?.adblueRate && errors?.adblueRate}
                        message={errors?.adblueRate}
                     />
                  </div>

                  <div>
                     <CustomInput
                        require
                        label="Trip Advance"
                        id="tripAdvance"
                        placeholder="Enter trip advance"
                        {...getFieldProps(`tripAdvance`)}
                        errors={touched?.tripAdvance && errors?.tripAdvance}
                        message={errors?.tripAdvance}
                     />
                  </div>

                  <div>
                     <CustomInput
                        require
                        label="Miscellaneous Rate"
                        id="miscellaneousRate"
                        placeholder="Enter miscellaneous rate"
                        {...getFieldProps(`miscellaneousRate`)}
                        errors={touched?.miscellaneousRate && errors?.miscellaneousRate}
                        message={errors?.miscellaneousRate}
                     />
                  </div>
               </>
            }
         />

         <div className="d-flex w-100 mb-2 justify-content-between align-items-end">
            <label className="text-bold">Lane details</label>
            <div className="">
               <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddButtonClick}
               >
                  <i className="fas fa-plus"></i> Add More
               </button>
            </div>
         </div>
         <CustomTable columns={columns} data={data} />
      </>
   );
};

export default Lane;
